/**
 * A drone's blades, heard (67.27 b): a soft, muted hum — two slightly detuned low tones and a sub through a warm
 * low-pass that breathes slowly (the blades), quieter than a real drone. One voice for each of the nearest few drones,
 * placed to its side, darker with distance, its pitch lifting in a strike's dive (`gunfireMix.hearDrone`). It runs
 * through the music's duck stage, so it sits under the music and dips under gunfire with it. Off until the owner has
 * heard it — on by default since decision 147 (the Lab's row retired; the owner: the noise is to be fixed later). Presentation only.
 */
import { getAudioContext, getDuckedBus, initAudio } from './sfx';
import type { DroneHumLevel } from './gunfireMix';

/** At most this many drones hum at once (the nearest). */
export const DRONE_VOICES = 4;

interface Voice { oscs: OscillatorNode[]; filter: BiquadFilterNode; gain: GainNode; side: StereoPannerNode; lfo: OscillatorNode }

let override: boolean | null = null;
/** Decision 147: on, its Lab row and URL flag retired; the setter is kept for a game or design toggle later (null = on). */
export function setDroneSound(on: boolean | null): void { override = on; }
export function droneSoundOn(): boolean { return override ?? true; }

export class DroneHum {
  private readonly voices = new Map<number, Voice>();

  /** This frame's audible drones (id → how it is heard); the rest fade out. The nearest DRONE_VOICES are kept. */
  update(heard: Array<{ id: number; level: DroneHumLevel }>): void {
    initAudio();
    const ctx = getAudioContext(), bus = getDuckedBus();
    if (!ctx || !bus) return;
    const keep = heard.slice().sort((a, b) => b.level.gain - a.level.gain).slice(0, DRONE_VOICES);
    const live = new Set(keep.map(k => k.id));
    const t = ctx.currentTime;
    for (const [id, v] of this.voices) if (!live.has(id)) { this.release(v, t); this.voices.delete(id); }
    for (const { id, level } of keep) {
      let v = this.voices.get(id);
      if (!v) { v = this.voice(ctx, bus); this.voices.set(id, v); }
      v.gain.gain.setTargetAtTime(level.gain, t, 0.12);
      v.filter.frequency.setTargetAtTime(level.cutoff, t, 0.2);
      v.side.pan.setTargetAtTime(level.pan, t, 0.1);
      const [a, b, sub] = v.oscs;
      a.frequency.setTargetAtTime(level.pitch, t, 0.25);
      b.frequency.setTargetAtTime(level.pitch * 1.006, t, 0.25);   // a slow beat between the two: the blades
      sub.frequency.setTargetAtTime(level.pitch / 2, t, 0.25);
    }
  }

  stop(): void {
    const ctx = getAudioContext();
    for (const v of this.voices.values()) this.release(v, ctx?.currentTime ?? 0);
    this.voices.clear();
  }

  private voice(ctx: AudioContext, bus: AudioNode): Voice {
    const gain = ctx.createGain(); gain.gain.value = 0;
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 600; filter.Q.value = 0.9;
    const side = ctx.createStereoPanner();
    filter.connect(gain); gain.connect(side); side.connect(bus);
    const osc = (type: OscillatorType, hz: number, level: number) => {
      const o = ctx.createOscillator(); o.type = type; o.frequency.value = hz;
      const g = ctx.createGain(); g.gain.value = level; o.connect(g); g.connect(filter); o.start();
      return o;
    };
    const oscs = [osc('sawtooth', 130, 0.35), osc('sawtooth', 130.8, 0.35), osc('sine', 65, 0.6)];
    // the blades' slow breath: the filter swings a little at a few hertz
    const lfo = ctx.createOscillator(); lfo.frequency.value = 5.5;
    const depth = ctx.createGain(); depth.gain.value = 140;
    lfo.connect(depth); depth.connect(filter.frequency); lfo.start();
    return { oscs, filter, gain, side, lfo };
  }

  private release(v: Voice, t: number): void {
    v.gain.gain.setTargetAtTime(0, t, 0.15);
    const end = t + 0.8;
    for (const o of [...v.oscs, v.lfo]) { try { o.stop(end); } catch { /* stopped */ } }
    setTimeout(() => { try { v.side.disconnect(); } catch { /* gone */ } }, 1000);
  }
}
